<template>
  <div class="toast-host" aria-live="polite" aria-relevant="additions">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="toast"
      :class="`toast-${toast.kind}`"
      :role="toast.kind === 'error' ? 'alert' : 'status'"
    >
      <div class="toast-glyph" aria-hidden="true">
        <VibelaMark
          v-if="toast.kind === 'success'"
          :size="22"
          :show-cleft="false"
        />
        <VibelaMark v-else-if="toast.kind === 'match'" :size="28" />
        <VibelaMark
          v-else-if="toast.kind === 'info'"
          :size="22"
          :show-cleft="false"
          class="toast-glyph-info"
        />
        <span v-else-if="toast.kind === 'loading'" class="toast-spinner"></span>
        <svg
          v-else-if="toast.kind === 'error'"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#C53030"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M12 2 L22 20 H2 Z" stroke-linejoin="round" />
          <path d="M12 8 v5" />
          <circle cx="12" cy="16" r="0.6" fill="#C53030" />
        </svg>
      </div>

      <div class="toast-body">
        <div class="toast-title">{{ toast.title }}</div>
        <div v-if="toast.description" class="toast-desc">{{ toast.description }}</div>
        <button
          v-if="toast.action"
          type="button"
          class="toast-action"
          @click="runAction(toast)"
        >
          {{ toast.action.label }}
        </button>
      </div>

      <button
        type="button"
        class="toast-close"
        aria-label="Schließen"
        @click="dismiss(toast.id)"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <path d="M6 6 L18 18 M18 6 L6 18" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import VibelaMark from './VibelaMark.vue';

const MAX_TOASTS = 3;
const DURATION = { error: 8000, match: 9000, default: 5000 };

let nextId = 0;

export default {
  name: 'ToastHost',
  components: { VibelaMark },
  data() {
    return {
      toasts: [],
      timers: {},
    };
  },
  created() {
    const make = (kind) => (title, options = {}) =>
      this.push({ kind, title, ...options });

    window.toast = {
      success: make('success'),
      error: make('error'),
      info: make('info'),
      match: make('match'),
      loading: (title, options = {}) =>
        this.push({ kind: 'loading', title, persistent: true, ...options }),
      dismiss: (id) => this.dismiss(id),
    };
    this.toastApi = window.toast;
  },
  beforeDestroy() {
    Object.keys(this.timers).forEach((id) => clearTimeout(this.timers[id]));
    if (window.toast === this.toastApi) {
      delete window.toast;
    }
  },
  methods: {
    push(toast) {
      const id = ++nextId;
      this.toasts = [...this.toasts, { ...toast, id }].slice(-MAX_TOASTS);

      if (!toast.persistent) {
        const ms = DURATION[toast.kind] || DURATION.default;
        this.timers[id] = setTimeout(() => this.dismiss(id), ms);
      }
      return id;
    },
    dismiss(id) {
      this.toasts = this.toasts.filter((toast) => toast.id !== id);
      clearTimeout(this.timers[id]);
      delete this.timers[id];
    },
    runAction(toast) {
      if (toast.action && typeof toast.action.onClick === 'function') {
        toast.action.onClick();
      }
      this.dismiss(toast.id);
    },
  },
};
</script>

<style scoped>
.toast-glyph-info {
  opacity: 0.6;
}

/*
 * app.css (bootstrap) ships its own .toast component and loads after
 * front.css, so on the SPA pages it wins and hides ours with opacity: 0.
 * Re-declare what it clobbers; the scoped attribute outweighs its selector.
 */
.toast {
  flex-basis: auto;
  max-width: none;
  width: 100%;
  opacity: 1;
  font-size: inherit;
  background: var(--cream);
  background-clip: border-box;
  border-width: 0 0 0 4px;
  border-style: solid;
  border-radius: 16px;
  box-shadow: 0 20px 40px -15px rgba(42, 24, 32, 0.2), 0 6px 16px -8px rgba(42, 24, 32, 0.12);
}

/* Bootstrap's border shorthand also reset these, so re-assert the variants. */
.toast-success,
.toast-loading,
.toast-match {
  border-left-color: var(--rose);
}

.toast-error {
  border-left-color: #C53030;
}

.toast-info {
  border-left-color: var(--mute);
}

.toast-match {
  background: linear-gradient(135deg, rgba(255, 139, 107, 0.08), rgba(230, 57, 115, 0.06));
}
</style>
